import ApiService from "@/core/services/api.service";
import { transferLoanRepaymentToSavings } from "@/core/services/loanTransaction.service";
import {
	TRANSFER_REPAYMENT_TO_SAVINGS,
	transferRepaymentToSavingsConfirmTitle,
} from "@/constants/transferRepaymentToSavingsCopy";

export function isSetOffTransactionMode(mode) {
	if (mode == null || mode === "") return false;
	const m = String(mode).toLowerCase().replace(/\s+/g, "");
	return m === "set-off" || m === "setoff";
}

/**
 * Completed loan repayment rows that are not Set-Off and not reversed.
 * If trans_status is present, it must be COMPLETED.
 */
export function canTransferRepaymentToSavings(trans) {
	if (!trans || trans.transaction_type !== "Loan Repayment") return false;
	if (isSetOffTransactionMode(trans.transaction_mode)) return false;
	if (trans.is_reversed === 1 || trans.is_reversed === true) return false;
	if (
		trans.trans_status != null &&
		trans.trans_status !== "" &&
		trans.trans_status !== "COMPLETED"
	) {
		return false;
	}
	return true;
}

export async function fetchMemberSavingsAccountsForTransfer(customerId) {
	if (!customerId) return [];
	const res = await ApiService.get(
		`/accounts/member/saving-shares/${customerId}/dropdown`
	);
	const accounts = res?.data?.accounts || [];
	return accounts.filter(
		(a) => a?.account_type?.base_type === "Savings"
	);
}

/**
 * Confirm + optional savings account + notes, then POST transfer API.
 * @param {Vue} vm — component instance (uses $Progress, displayNumber, global swal/toast)
 * @param {{ trans: object, customerId: number|string|null, onSuccess?: () => void }} options
 */
export async function promptTransferLoanRepaymentToSavings(vm, options) {
	const { trans, customerId, onSuccess } = options;
	if (!trans?.id) return;

	vm.$Progress.start();
	let savingsAccounts = [];
	try {
		savingsAccounts = await fetchMemberSavingsAccountsForTransfer(customerId);
	} catch (e) {
		// still allow transfer; backend can pick default savings account
	} finally {
		vm.$Progress.finish();
	}

	const formatAmount = (n) => vm.displayNumber(n);
	const multiple = savingsAccounts.length > 1;

	let html = `<p class="text-start small mb-2">${TRANSFER_REPAYMENT_TO_SAVINGS.description} This applies to the repayment group and includes any overpayment recorded in the same group.</p>`;
	if (multiple) {
		html += `<label class="d-block text-start mb-1 small">Savings account</label><select id="swal-savings-account" class="swal2-select form-control">`;
		savingsAccounts.forEach((a) => {
			const label = `${a.account_type?.name || "Savings"} (${formatAmount(
				a.account_balance
			)})`;
			html += `<option value="${a.id}">${label}</option>`;
		});
		html += `</select>`;
	}
	html += `<label class="d-block text-start mt-2 mb-1 small">Notes (optional)</label><textarea id="swal-transfer-notes" class="swal2-textarea form-control" rows="2" placeholder="Notes"></textarea>`;

	swal
		.fire({
			title: transferRepaymentToSavingsConfirmTitle(),
			html,
			icon: "question",
			showCancelButton: true,
			confirmButtonText: "Yes, transfer",
			cancelButtonText: "Cancel",
			allowOutsideClick: true,
			focusConfirm: false,
			customClass: {
				confirmButton:
					"btn btn-size akkurate-green-btn modal-btn-width text-white me-2",
				cancelButton:
					"btn btn-size akkurate-danger-btn modal-btn-width text-white",
			},
			preConfirm: () => {
				const notesEl = document.getElementById("swal-transfer-notes");
				const notes = notesEl ? notesEl.value.trim() : "";
				let savings_account_id;
				if (multiple) {
					const sel = document.getElementById("swal-savings-account");
					savings_account_id = sel ? Number(sel.value) : undefined;
				}
				return { notes, savings_account_id };
			},
		})
		.then(async (result) => {
			if (!result.value) return;
			const { notes, savings_account_id } = result.value;
			const body = { transaction_id: trans.id };
			if (notes) body.notes = notes;
			if (multiple && savings_account_id != null) {
				body.savings_account_id = savings_account_id;
			}
			vm.$Progress.start();
			try {
				const respp = await transferLoanRepaymentToSavings(body);
				vm.$Progress.finish();
				toast.fire({
					icon: "success",
					title: respp.data.message,
				});
				if (onSuccess) onSuccess();
			} catch (error) {
				vm.$Progress.fail();
				const msg =
					error?.response?.data?.message ||
					error?.response?.statusText ||
					error?.message ||
					"Something went wrong";
				swal.fire({
					icon: "error",
					title: "Unable to transfer",
					text: msg,
					showCancelButton: false,
					confirmButtonText: "OK",
					buttonsStyling: false,
					customClass: {
						confirmButton:
							"btn btn-size akkurate-green-btn modal-btn-width text-white me-2",
					},
				});
			}
		});
}
